import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { DisponibilidadeSummary } from "@/utils/disponibilidadeUtils";

// As views/RPCs de disponibilidade (sql/135+138) ainda não estão no types.ts gerado -> cast.
const availabilityDb = supabase as unknown as {
  from: (table: string) => any;
  rpc: (functionName: string, args: Record<string, unknown>) => any;
};

/**
 * Lê v_availability_summary em LOTE para uma lista de freelancer ids. Uma request só.
 * Freelancer sem sinal não vem na view -> quem consome trata ausência como "desconhecido".
 * queryKey por shape (["disponibilidade","summary", ids]) p/ não colidir com outras leituras.
 */
export function useDisponibilidadeBatch(freelancerIds: (string | undefined | null)[]) {
  const uniqueIds = Array.from(
    new Set(freelancerIds.filter((id): id is string => typeof id === "string" && id.length > 0))
  ).sort();

  return useQuery({
    queryKey: ["disponibilidade", "summary", uniqueIds],
    enabled: uniqueIds.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Map<string, DisponibilidadeSummary>> => {
      const summaryByFreelancer = new Map<string, DisponibilidadeSummary>();
      // PostgREST limita o tamanho da URL; quebra em lotes.
      const CHUNK_SIZE = 300;
      for (let start = 0; start < uniqueIds.length; start += CHUNK_SIZE) {
        const chunk = uniqueIds.slice(start, start + CHUNK_SIZE);
        const { data, error } = await availabilityDb
          .from("v_availability_summary")
          .select("*")
          .in("freelancer_id", chunk);
        if (error) throw error;
        for (const row of (data ?? []) as DisponibilidadeSummary[]) {
          summaryByFreelancer.set(row.freelancer_id, row);
        }
      }
      return summaryByFreelancer;
    },
  });
}

/** Disponibilidade de UM freelancer (detalhe). */
export function useDisponibilidade(freelancerId?: string | null) {
  return useQuery({
    queryKey: ["disponibilidade", "summary", "one", freelancerId],
    enabled: !!freelancerId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<DisponibilidadeSummary | null> => {
      const { data, error } = await availabilityDb
        .from("v_availability_summary")
        .select("*")
        .eq("freelancer_id", freelancerId)
        .maybeSingle();
      if (error) throw error;
      return (data as DisponibilidadeSummary) ?? null;
    },
  });
}

/** Contagens da base por status efetivo, temperatura e valores de cada eixo (1 query). */
export interface DisponibilidadeCounts {
  porEfetiva: Record<string, number>;
  porTemperatura: Record<string, number>;
  /** Espectro de temperatura DENTRO de cada status efetivo (tooltip dos chips). */
  porEfetivaTemperatura: Record<string, Record<string, number>>;
  porEixo: {
    disp_emprego: Record<string, number>;
    disp_interesse: Record<string, number>;
    disp_relacao: Record<string, number>;
  };
  totalBase: number;
}

/**
 * Contagem da base inteira (a "avaliação da base"). v_availability_summary só tem
 * quem tem sinal; os demais entram como 'desconhecido' por eixo/status e 'sem_info'
 * na temperatura (mesma regra de COALESCE do filtro server-side, M171).
 */
export function useDisponibilidadeCounts() {
  return useQuery({
    queryKey: ["disponibilidade", "counts"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<DisponibilidadeCounts> => {
      const { data, error } = await availabilityDb
        .from("v_availability_summary")
        .select("disponibilidade_efetiva, emprego, interesse, relacao, temperatura");
      if (error) throw error;
      const rows = (data ?? []) as {
        disponibilidade_efetiva: string;
        emprego: string | null;
        interesse: string | null;
        relacao: string | null;
        temperatura: string | null;
      }[];

      const soma = (mapa: Record<string, number>, chave: string) => {
        mapa[chave] = (mapa[chave] ?? 0) + 1;
      };
      const counts: DisponibilidadeCounts = {
        porEfetiva: {},
        porTemperatura: {},
        porEfetivaTemperatura: {},
        porEixo: { disp_emprego: {}, disp_interesse: {}, disp_relacao: {} },
        totalBase: 0,
      };
      for (const row of rows) {
        const efetiva = row.disponibilidade_efetiva ?? "desconhecido";
        const temperatura = row.temperatura ?? "sem_info";
        soma(counts.porEfetiva, efetiva);
        soma(counts.porTemperatura, temperatura);
        counts.porEfetivaTemperatura[efetiva] = counts.porEfetivaTemperatura[efetiva] ?? {};
        soma(counts.porEfetivaTemperatura[efetiva], temperatura);
        soma(counts.porEixo.disp_emprego, row.emprego ?? "desconhecido");
        soma(counts.porEixo.disp_interesse, row.interesse ?? "desconhecido");
        soma(counts.porEixo.disp_relacao, row.relacao ?? "desconhecido");
      }

      const { count: totalFreelancers } = await availabilityDb
        .from("freelancers")
        .select("id", { count: "exact", head: true });
      counts.totalBase = totalFreelancers ?? rows.length;
      const semSinal = Math.max(counts.totalBase - rows.length, 0);
      if (semSinal > 0) {
        counts.porEfetiva["desconhecido"] = (counts.porEfetiva["desconhecido"] ?? 0) + semSinal;
        counts.porTemperatura["sem_info"] = (counts.porTemperatura["sem_info"] ?? 0) + semSinal;
        counts.porEfetivaTemperatura["desconhecido"] = counts.porEfetivaTemperatura["desconhecido"] ?? {};
        counts.porEfetivaTemperatura["desconhecido"]["sem_info"] =
          (counts.porEfetivaTemperatura["desconhecido"]["sem_info"] ?? 0) + semSinal;
        for (const eixo of ["disp_emprego", "disp_interesse", "disp_relacao"] as const) {
          counts.porEixo[eixo]["desconhecido"] = (counts.porEixo[eixo]["desconhecido"] ?? 0) + semSinal;
        }
      }
      return counts;
    },
  });
}

export interface Reconsulta {
  id: string;
  due_at: string;
  reason: string;
  status: string;
  note: string | null;
}

/**
 * Reconsulta pendente mais próxima de UM freelancer (availability_checks,
 * reason='data_prevista'). "Data prevista de disponibilidade / reconsultar em".
 * Retorna a de menor due_at ainda pendente, ou null.
 */
export function useReconsulta(freelancerId?: string | null) {
  return useQuery({
    queryKey: ["reconsulta", freelancerId],
    enabled: !!freelancerId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<Reconsulta | null> => {
      const { data, error } = await availabilityDb
        .from("availability_checks")
        .select("id, due_at, reason, status, note")
        .eq("freelancer_id", freelancerId)
        .eq("reason", "data_prevista")
        .eq("status", "pendente")
        .order("due_at", { ascending: true })
        .limit(1);
      if (error) throw error;
      return ((data ?? [])[0] as Reconsulta) ?? null;
    },
  });
}

export interface ReconsultaItem {
  id: string;
  freelancer_id: string;
  due_at: string;
  status: "pendente" | "feito" | "cancelado";
  note: string | null;
  resolved_at: string | null;
  nome: string;
  email: string | null;
  telefone: string | null;
  areas: string[] | null;
  cidade: string | null;
  portfolio_url: string | null;
  linkedin_url: string | null;
}

const RECONSULTA_HISTORICO_DIAS = 45;

/**
 * Todas as reconsultas do board (availability_checks, reason='data_prevista'):
 * pendentes + resolvidas (feito/cancelado) dos últimos ~45 dias, já com os dados
 * de exibição do freelancer. Alimenta o kanban de Reconsultas em Atividades.
 */
export function useReconsultas() {
  return useQuery({
    queryKey: ["reconsulta", "board"],
    staleTime: 2 * 60 * 1000,
    queryFn: async (): Promise<ReconsultaItem[]> => {
      const { data: checks, error } = await availabilityDb
        .from("availability_checks")
        .select("id, freelancer_id, due_at, status, note, resolved_at")
        .eq("reason", "data_prevista")
        .order("due_at", { ascending: true })
        .limit(500);
      if (error) throw error;
      const rows = (checks ?? []) as Omit<
        ReconsultaItem,
        "nome" | "email" | "telefone" | "areas" | "cidade" | "portfolio_url" | "linkedin_url"
      >[];

      // Pendentes sempre; feito/cancelado só se resolvidas há pouco (não polui o board).
      const corte = Date.now() - RECONSULTA_HISTORICO_DIAS * 24 * 60 * 60 * 1000;
      const filtradas = rows.filter(
        (row) =>
          row.status === "pendente" ||
          (row.resolved_at ? new Date(row.resolved_at).getTime() >= corte : false)
      );

      const ids = Array.from(new Set(filtradas.map((row) => row.freelancer_id)));
      const freelancerById = new Map<string, Record<string, unknown>>();
      if (ids.length) {
        const { data: freis, error: freiError } = await availabilityDb
          .from("freelancers")
          .select("id, nome, email, telefone, areas, cidade, portfolio_url, linkedin_url")
          .in("id", ids);
        if (freiError) throw freiError;
        for (const freelancer of (freis ?? []) as Record<string, unknown>[]) {
          freelancerById.set(freelancer.id as string, freelancer);
        }
      }

      return filtradas.map((row) => {
        const freelancer = freelancerById.get(row.freelancer_id) ?? {};
        return {
          ...row,
          nome: (freelancer.nome as string) ?? "(sem nome)",
          email: (freelancer.email as string) ?? null,
          telefone: (freelancer.telefone as string) ?? null,
          areas: (freelancer.areas as string[]) ?? null,
          cidade: (freelancer.cidade as string) ?? null,
          portfolio_url: (freelancer.portfolio_url as string) ?? null,
          linkedin_url: (freelancer.linkedin_url as string) ?? null,
        };
      });
    },
  });
}

/** Muda o status de UMA reconsulta (feito/cancelado/reabrir). */
export function useAtualizarReconsultaStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      checkId,
      status,
    }: {
      checkId: string;
      status: "feito" | "cancelado" | "pendente";
    }) => {
      const patch: Record<string, unknown> = {
        status,
        resolved_at: status === "pendente" ? null : new Date().toISOString(),
      };
      const { error } = await availabilityDb
        .from("availability_checks")
        .update(patch)
        .eq("id", checkId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["reconsulta"] });
    },
  });
}

export interface SinalDisponibilidade {
  id: string;
  dimension: string;
  value: string;
  source_type: string;
  author_kind: string;
  confidence: string;
  observed_at: string;
  valid_from: string;
  valid_until: string | null;
  quote: string | null;
  observation: string | null;
  review_status: string;
}

/** Eventos crus (sinais) de UM freelancer, mais recente primeiro. Alimenta o histórico completo. */
export function useDisponibilidadeSinais(freelancerId?: string | null) {
  return useQuery({
    queryKey: ["disponibilidade", "sinais", freelancerId],
    enabled: !!freelancerId,
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<SinalDisponibilidade[]> => {
      const { data, error } = await availabilityDb
        .from("availability_signals")
        .select(
          "id, dimension, value, source_type, author_kind, confidence, observed_at, valid_from, valid_until, quote, observation, review_status"
        )
        .eq("freelancer_id", freelancerId)
        .is("deleted_at", null)
        .order("observed_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as SinalDisponibilidade[];
    },
  });
}

export interface TimelineSegment {
  from: string;
  to: string | null;
  eixos: Record<string, { value: string; source: string; confidence: string | null }>;
  carga: {
    carga: number;
    capacity: number;
    headroom: number;
    next_freeing: string | null;
    fully_free: string | null;
    ultimo_projeto: boolean;
  } | null;
}
export interface TimelineData {
  freelancer_id: string;
  window: { from: string; to: string };
  now: string;
  segments: TimelineSegment[];
}

/** Timeline histórica (replay as-of) de UM freelancer via RPC get_freelancer_timeline (sql/137). */
export function useFreelancerTimeline(freelancerId?: string | null, monthsBack = 18) {
  return useQuery({
    queryKey: ["disponibilidade", "timeline", freelancerId, monthsBack],
    enabled: !!freelancerId,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<TimelineData | null> => {
      const windowEnd = new Date();
      const windowStart = new Date();
      windowStart.setMonth(windowStart.getMonth() - monthsBack);
      const { data, error } = await availabilityDb.rpc("get_freelancer_timeline", {
        fid: freelancerId,
        win_from: windowStart.toISOString(),
        win_to: windowEnd.toISOString(),
      });
      if (error) throw error;
      return (data as TimelineData) ?? null;
    },
  });
}
